// Works out what an element holder should show, based on whether it is hovered,
// selected, nested inside another holder, or caught up in a drag.
//
// Containers work differently to everything else, which is the heart of this:
//
//  - A top level container gets its grip handle and controls from being hovered,
//    and nothing else. Hovering one always gives it them, even if you had
//    previously clicked something in another container. Because only one
//    container can be hovered at a time, the one you leave gives them up as the
//    one you arrive at takes them, so two are never showing at once.
//  - It keeps them while the pointer moves over its own children, but gives them
//    up while one of those children is selected, so the two do not fight.
//  - Everything nested, whether a plain element or a container inside another
//    container, gets them only from being clicked.
//  - While something is selected, a container you hover still gets its grip
//    handle, but not its controls. Only the selected thing shows controls, so
//    there is never a second set competing with it.
//  - Hovering anything gives a light outline. Selecting gives a strong one.

export const DRAG_DROP_HOLDER_CLASS = 'dndEditorv2-elementHolder'

// True if this holder sits inside another holder. We read it from the page
// rather than from the element path, so it stays right after things are dragged
// around and the paths change.
export function isHolderNested(element) {
    return !!(element && element.parentElement && element.parentElement.closest('.' + DRAG_DROP_HOLDER_CLASS))
}

export function computeHolderChrome({
    mouseOver,
    nestedHoverCount,
    isNestedHolder,
    path,
    selectedPath,
    isDragging,
    isAnyDragActive,
    activeDrag
}) {
    const isThisDrag = isDragging === true || !!activeDrag
    const dragging = isThisDrag || !!isAnyDragActive
    const isSelected = !!path && selectedPath === path
    // One of our own children is selected, so it takes the chrome off us until it
    // is deselected. Child paths are always our path plus ".elements[n]".
    const descendantSelected = !!path && !!selectedPath && selectedPath.indexOf(path + '.') === 0

    // Something is selected somewhere, so it is the only thing allowed to show
    // controls.
    const anythingSelected = !!selectedPath

    // A top level container earns its chrome by being hovered. Anything nested
    // has to be clicked instead.
    const hoverChrome = !isNestedHolder && !!mouseOver && !descendantSelected && !dragging
    const clickChrome = !!isNestedHolder && isSelected

    // The light hover outline goes to the innermost holder under the pointer. A
    // container showing its chrome keeps its outline too, so the grip handle
    // always has a box around it, even while the pointer is over a child.
    // The item being dragged keeps that outline as well — otherwise the HTML5
    // ghost and the live drop slot both look like a bare block.
    const showOutline =
        (isThisDrag && !isSelected) || (!!mouseOver && !dragging && !isSelected && (!nestedHoverCount || hoverChrome))

    const showDragBar = hoverChrome || clickChrome || isThisDrag

    // Copy / delete / reorder sit under the block and get in the way while
    // something is being dragged. The grip is the only chrome that stays.
    const showControls = !dragging && (clickChrome || (hoverChrome && (!anythingSelected || isSelected)))

    return {
        isSelected,
        showControls,
        showDragBar,
        // Used to lift whichever holder is showing something above its neighbours.
        showChrome: showDragBar || showControls,
        showOutline
    }
}
